import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Landmark, LogOut, FolderPlus, Upload, Image as ImageIcon, KeyRound, Users } from "lucide-react";
import { api, formatErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Toolbar } from "@/components/Toolbar";
import { GroupSection } from "@/components/GroupSection";
import { EntryDialog } from "@/components/EntryDialog";
import { GroupDialog } from "@/components/GroupDialog";
import { AccountDialog } from "@/components/AccountDialog";
import { DailyEntryDialog } from "@/components/DailyEntryDialog";
import { ImportDialog } from "@/components/ImportDialog";
import { ReportDialog } from "@/components/ReportDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Link } from "react-router-dom";
import { exportData } from "@/lib/exporter";

const Stat = ({ label, value, testid, mono }) => (
  <div className="bg-white border rounded-xl p-4">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p data-testid={testid} className={`mt-1 text-2xl font-bold ${mono ? "font-mono text-emerald-700" : "font-head"}`}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [groups, setGroups] = useState([]);
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [entryDlg, setEntryDlg] = useState(null);
  const [groupDlg, setGroupDlg] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accDlg, setAccDlg] = useState(null);
  const [dailyGroup, setDailyGroup] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const [g, e, a] = await Promise.all([api.get("/groups"), api.get("/entries", { params }), api.get("/accounts")]);
    setGroups(g.data);
    setAccounts(a.data);
    setEntries(e.data);
  }, [from, to]);

  useEffect(() => { load().catch((e) => toast.error(formatErr(e))); }, [load]);

  const { visibleGroups, byGroup } = useMemo(() => {
    const s = q.trim().toLowerCase();
    const map = {};
    const vis = groups.filter((g) => {
      const gMatch = g.name.toLowerCase().includes(s);
      map[g.id] = entries.filter((e) => e.group_id === g.id &&
        (!s || gMatch || e.site_name.toLowerCase().includes(s) || e.user_code.toLowerCase().includes(s)));
      return !s || gMatch || map[g.id].length > 0;
    });
    return { visibleGroups: vis, byGroup: map };
  }, [groups, entries, q]);

  const shown = visibleGroups.flatMap((g) => byGroup[g.id]);
  const grand = shown.reduce((s, e) => s + e.amount, 0);
  const sites = new Set(shown.map((e) => e.site_name.toLowerCase())).size;

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const saveEntry = (d) => run(async () => {
    await api.put(`/entries/${d.id}`, { amount: d.amount, entry_date: d.entry_date });
    setEntryDlg(null);
  }, "Entry updated");

  const saveDaily = (body) => run(async () => {
    await api.post("/entries/bulk", body);
    setDailyGroup(null);
  }, `${body.items.length} entries saved`);

  const saveAccount = (f) => run(async () => {
    const body = { group_id: f.group_id, site_name: f.site_name, user_code: f.user_code };
    if (f.id) await api.put(`/accounts/${f.id}`, body); else await api.post("/accounts", body);
    setAccDlg(null);
  }, "Site saved");

  const doImport = async (rows) => {
    try {
      const { data } = await api.post("/import", { rows });
      toast.success(`Imported: ${data.groups} groups, ${data.sites} sites, ${data.entries} entries`);
      setImportOpen(false);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const accountActionsFor = (gid) => ({ onAdd: () => setAccDlg({ group_id: gid }), onEdit: setAccDlg, onDelete: delAccount });

  const delAccount = (a) => window.confirm(`Remove site "${a.site_name}" and its entries?`) &&
    run(() => api.delete(`/accounts/${a.id}`), "Site removed");

  const saveGroup = (name) => run(async () => {
    if (groupDlg?.id) await api.put(`/groups/${groupDlg.id}`, { name }); else await api.post("/groups", { name });
    setGroupDlg(null);
  }, "Group saved");

  const delGroup = (g) => window.confirm(`Delete group "${g.name}" and all its entries?`) &&
    run(() => api.delete(`/groups/${g.id}`), "Group deleted");
  const delEntry = (e) => window.confirm("Delete this entry?") && run(() => api.delete(`/entries/${e.id}`), "Entry deleted");

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <nav className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-head text-xl font-bold tracking-tight"><Landmark className="w-5 h-5 text-sky-400" />LEDGERLINE</div>
          <div className="flex items-center gap-3 text-sm">
            <span data-testid="user-email" className="hidden sm:inline text-slate-400">{user.email}</span>
            {user.role === "admin" && (
              <Link to="/admin/users" data-testid="admin-users-link" className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"><Users className="w-4 h-4" />Users</Link>
            )}
            <button data-testid="change-password-btn" onClick={() => setCpOpen(true)} className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"><KeyRound className="w-4 h-4" /><span className="hidden sm:inline">Password</span></button>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-head text-4xl sm:text-5xl font-bold tracking-tight">Data Entry</h1>
            <p className="text-sm text-slate-500 mt-1">Group-wise, date-wise settlement records</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="report-image-btn" onClick={() => setReportOpen(true)}><ImageIcon className="w-4 h-4 mr-2" />Report image</Button>
            <Button variant="outline" data-testid="import-excel-btn" onClick={() => setImportOpen(true)}><Upload className="w-4 h-4 mr-2" />Import Excel</Button>
            <Button variant="outline" data-testid="add-group-btn" onClick={() => setGroupDlg({})}><FolderPlus className="w-4 h-4 mr-2" />New group</Button>
          </div>
        </div>
        <div data-testid="grand-totals-summary" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Groups" value={visibleGroups.length} testid="stat-groups" />
          <Stat label="Sites" value={sites} testid="stat-sites" />
          <Stat label="Entries" value={shown.length} testid="stat-entries" />
          <Stat label="Grand total" value={inr(grand)} testid="grand-total-amount" mono />
        </div>
        <Toolbar q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} onExport={(t) => exportData(visibleGroups, byGroup, t)} />
        <div className="space-y-4">
          {groups.length === 0 && (
            <div data-testid="empty-state" className="border border-dashed rounded-xl p-12 text-center bg-white">
              <p className="font-head text-2xl font-semibold">No groups yet</p>
              <p className="text-sm text-slate-500 mt-1">Create a group, add its sites once, then enter amounts date-wise.</p>
            </div>
          )}
          {visibleGroups.map((g) => (
            <GroupSection key={g.id} group={g} entries={byGroup[g.id]}
              accounts={accounts.filter((a) => a.group_id === g.id)}
              accountActions={accountActionsFor(g.id)}
              onAdd={() => setDailyGroup(g)} onEditGroup={() => setGroupDlg(g)} onDeleteGroup={() => delGroup(g)}
              onEdit={(e) => setEntryDlg(e)} onDelete={delEntry} />
          ))}
        </div>
      </main>
      <EntryDialog open={!!entryDlg} onOpenChange={(o) => !o && setEntryDlg(null)} initial={entryDlg} onSave={saveEntry} />
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} groups={groups} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImport={doImport} />
      <AccountDialog open={!!accDlg} onOpenChange={(o) => !o && setAccDlg(null)} initial={accDlg} onSave={saveAccount} />
      <DailyEntryDialog group={dailyGroup} accounts={accounts.filter((a) => a.group_id === dailyGroup?.id)}
        onOpenChange={(o) => !o && setDailyGroup(null)} onSave={saveDaily} />
      <GroupDialog open={!!groupDlg} onOpenChange={(o) => !o && setGroupDlg(null)} initial={groupDlg} onSave={saveGroup} />
    </div>
  );
}
